import React, { useState, useEffect, useRef } from "react";
import { Plus } from "lucide-react";

/**
 * FunctionPicker (SOUND-504) — an "＋ Add" chip that opens a lightweight menu of
 * assignable functions (from the active registry, minus what's already
 * assigned). Ticketing-grammar dropdown; emits the chosen `functionKey`.
 */
export default function FunctionPicker({ functions = [], assigned = [], onPick, disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const available = functions.filter(
    (f) => f.active !== false && !assigned.includes(f.functionKey)
  );

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="badge badge-muted transition-colors hover:bg-muted disabled:opacity-50"
      >
        <Plus className="h-3 w-3" />
        Add
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-[calc(100%+4px)] z-30 max-h-56 min-w-[200px] animate-scale-in overflow-y-auto rounded-lg border border-border bg-card p-1.5 shadow-lg"
        >
          {available.length === 0 ? (
            <div className="px-2 py-2 text-xs text-muted-foreground">
              All functions assigned.
            </div>
          ) : (
            available.map((f) => (
              <button
                key={f.functionKey}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onPick?.(f.functionKey);
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                <span className="min-w-0 flex-1 truncate">{f.label}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{f.functionKey}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
